import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

export interface NavItem { to: string; label: string; end?: boolean; badge?: number }

const ICONS: Record<string, string> = {
  '/home': 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 4v5l3 2',
  '/sessions': 'M7 3v4m10-4v4M4 10h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zM8 14h2m4 0h2m-8 3h2',
  '/billing': 'M4 6h15v14H4zM4 6V4h12v2m-1 6h5v5h-5z',
  '/clients': 'M16 20v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2m6-9a4 4 0 1 0 0-8 4 4 0 0 0 0 8m8 2a4 4 0 0 1 3 4v3',
  '/tasks': 'M9 4H5v17h14V4h-4M9 3h6v4H9zM8 12l2 2 5-5m-7 9h7',
  '/todos': 'M4 6h2l1 1 2-2M4 12h2l1 1 2-2M4 18h2l1 1 2-2M13 6h8M13 12h8M13 18h8',
  '/family': 'M3 11l9-8 9 8M5 10v11h14V10m-10 11v-7h6v7',
  '/hq': 'M4 21V4h10v17M14 10h6v11M7 8h4m-4 4h4m-4 4h4M2 21h20',
  '/voucher': 'M12 3l8 4v6c0 4-8 8-8 8s-8-4-8-8V7zM8 12l3 3 5-6',
  '/courses': 'M3 5h6a3 3 0 0 1 3 3v12a2 2 0 0 0-2-2H3zM21 5h-6a3 3 0 0 0-3 3v12a2 2 0 0 1 2-2h7z',
  '/programs': 'M4 5h16v4H4zM4 11h16v4H4zM4 17h10v3H4z',
  '/center-courses': 'M3 5h6a3 3 0 0 1 3 3v12a2 2 0 0 0-2-2H3zM21 5h-6a3 3 0 0 0-3 3v12a2 2 0 0 1 2-2h7z',
  '/teachers': 'M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8m-7 9v-1a6 6 0 0 1 6-6h2m6 7a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2.5-.5L21 20',
  '/teacher-desk': 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M4 21v-1a7 7 0 0 1 7-7h2a7 7 0 0 1 7 7v1',
  '/accounting': 'M6 3h12v18H6zM9 7h6M9 11h1m2 0h1m2 0h0M9 14h1m2 0h1m2 0h0M9 17h1m2 0h1m2 0h0',
  more: 'M4 4h5v5H4zM15 4h5v5h-5zM4 15h5v5H4zM15 15h5v5h-5z',
  default: 'M6 3h9l4 4v14H6zM14 3v5h5M9 12h7m-7 4h7',
};

export function NavIcon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICONS[path] ?? ICONS.default} />
    </svg>
  );
}

const MOBILE = '(max-width:760px)';

/** 주 메뉴: 넓은 화면은 왼쪽(위쪽) 메뉴, 휴대폰은 아래 고정 메뉴 + 5개가 넘으면 "전체 메뉴" 시트. */
export function MainNav({ items }: { items: NavItem[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { if (!open) setQuery(''); }, [open]);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);
  useEffect(() => {
    const mq = matchMedia(MOBILE);
    const change = () => { if (!mq.matches) setOpen(false); };
    mq.addEventListener('change', change);
    return () => mq.removeEventListener('change', change);
  }, []);
  if (!items.length) return null;

  const more = items.length > 5;
  const primary = more ? items.slice(0, 4) : items;
  const q = query.trim().toLowerCase();
  const filtered = items.filter((i) => i.label.toLowerCase().includes(q));
  const link = (i: NavItem, icon: boolean, onClick?: () => void) => (
    <li key={i.to}><NavLink to={i.to} end={i.end} onClick={onClick}>{icon && <NavIcon path={i.to} />}<span>{i.label}</span>{Boolean(i.badge) && <span className="nav-badge" aria-label={`새 소식 ${i.badge}건`}>{i.badge}</span>}</NavLink></li>
  );

  return (
    <>
      <nav aria-label="주 메뉴" className="nav-desktop"><ul className="nav">{items.map((i) => link(i, false))}</ul></nav>
      <nav aria-label="휴대폰 주 메뉴" className="mobile-nav">
        <ul style={{ '--nav-count': primary.length + (more ? 1 : 0) } as CSSProperties}>
          {primary.map((i) => link(i, true))}
          {more && (
            <li>
              <button type="button" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open} aria-controls="mobile-menu-dialog">
                <NavIcon path="more" /><span>전체 메뉴</span>
              </button>
            </li>
          )}
        </ul>
      </nav>
      <dialog id="mobile-menu-dialog" ref={ref} className="dialog mobile-menu-dialog" onClose={() => setOpen(false)} aria-labelledby="mobile-menu-title">
        <div className="mobile-menu-body">
          <div className="mobile-menu-head">
            <h2 id="mobile-menu-title">전체 메뉴</h2>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>닫기</button>
          </div>
          <label className="mobile-menu-search">
            <span>메뉴 검색</span>
            <input type="search" value={query} placeholder="예: 일정, 이용자, 수납" onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
          </label>
          <nav aria-label="전체 업무 메뉴"><ul className="mobile-menu-links">{filtered.map((i) => link(i, true, () => setOpen(false)))}</ul></nav>
          {!filtered.length && <p role="status" className="mobile-menu-empty">일치하는 메뉴가 없습니다.</p>}
        </div>
      </dialog>
    </>
  );
}

/** 휴대폰에서는 접었다 펴는 상자, 넓은 화면에서는 늘 펼친 상태. */
export function MobileFilters({ label, children }: { label: string; children: ReactNode }) {
  const [mobile, setMobile] = useState(() => matchMedia(MOBILE).matches);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const mq = matchMedia(MOBILE);
    const change = () => setMobile(mq.matches);
    mq.addEventListener('change', change);
    return () => mq.removeEventListener('change', change);
  }, []);
  return (
    <details className="mobile-filter" open={!mobile || expanded} onToggle={(e) => { if (mobile) setExpanded(e.currentTarget.open); }}>
      <summary><NavIcon path="/sessions" /><span>{label}</span><span className="mobile-filter-label">{expanded ? '접기' : '펼치기'}</span></summary>
      {children}
    </details>
  );
}
